// Keyboard stepping for the recipe's numeric fields.
//
// The operators work in thousandths on the roll registers and in whole units on
// the line-up fields, so a single step size fits neither. Instead the arrow keys
// step the digit the caret sits on: put the caret on the tenths and Up moves by
// 0.1, put it on the tens and Up moves by 10. Moving the caret with Left/Right
// is therefore how the step size is chosen, and normal typing keeps working.

export interface SteppedValue {
	value: string;
	/** Caret position that keeps the same digit under the cursor. */
	caret: number;
}

export interface StepLimits {
	min?: number | null;
	max?: number | null;
}

const DIGIT = /[0-9]/;

const decimalsOf = (s: string): number => {
	const dot = s.indexOf(".");
	return dot === -1 ? 0 : s.length - dot - 1;
};

// The digit the caret is standing on: the one to its left, or the first digit
// when the caret is at the very start.
const digitIndexForCaret = (s: string, caret: number): number => {
	if (!s) return -1;
	let i = Math.min(Math.max(caret - 1, 0), s.length - 1);
	while (i >= 0 && !DIGIT.test(s[i])) i--;
	return i >= 0 ? i : s.search(DIGIT);
};

// Power of ten of the digit at `index`: 0 = units, 1 = tens, -1 = tenths.
const exponentAt = (s: string, index: number): number => {
	const dot = s.indexOf(".");
	if (dot === -1) return s.length - 1 - index;
	return index < dot ? dot - 1 - index : dot - index;
};

// Inverse of exponentAt: where the caret goes so the same place value stays
// under it after the value grew or shrank a digit (9.9 -> 10.0).
const caretForExponent = (s: string, exponent: number): number => {
	const dot = s.indexOf(".");
	const index =
		exponent >= 0
			? (dot === -1 ? s.length - 1 : dot - 1) - exponent
			: (dot === -1 ? s.length : dot) - exponent;
	return Math.min(Math.max(index + 1, 0), s.length);
};

export const stepNumericValue = (
	raw: string,
	caret: number,
	direction: 1 | -1,
	limits?: StepLimits,
): SteppedValue | null => {
	const trimmed = raw.trim();
	// An empty field starts from zero and steps by one, the way a spin button does.
	const base = trimmed === "" || trimmed === "-" ? "0" : trimmed;
	const current = Number(base);
	if (!Number.isFinite(current)) return null;

	const index = digitIndexForCaret(base, trimmed === "" ? 1 : caret);
	if (index < 0) return null;

	const exponent = exponentAt(base, index);
	let next = current + direction * 10 ** exponent;

	let clamped = false;
	if (limits?.min != null && next < limits.min) {
		next = limits.min;
		clamped = true;
	}
	if (limits?.max != null && next > limits.max) {
		next = limits.max;
		clamped = true;
	}

	// Binary floating point turns 9.9 + 0.1 into 10.000000000000002, so the
	// number of decimals is decided here and toFixed does the rounding. A clamped
	// value keeps its own precision instead: a limit of 0.05 must not land on 0.1.
	const decimals = Math.max(
		decimalsOf(base),
		exponent < 0 ? -exponent : 0,
		clamped ? decimalsOf(String(next)) : 0,
	);

	const value = next.toFixed(decimals);
	return { value, caret: caretForExponent(value, exponent) };
};
